import { useCallback, useMemo } from "react";

const SHOW_PAGES_COUNT = 6;

const SIBLING_COUNT = 3;

const DOTS = "...";

const range = (start: number, end: number) => {
  const length = end - start + 1;

  return Array.from({ length }, (_, idx) => idx + start);
};

type Props = {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

const useManagePagination = ({
  currentPage,
  pageCount,
  onPageChange,
}: Props) => {
  const pagesRange = useMemo(() => {
    if (pageCount <= SHOW_PAGES_COUNT) {
      return range(1, pageCount);
    }

    const leftSiblingIndex = Math.max(currentPage - SIBLING_COUNT, 1);
    const rightSiblingIndex = Math.min(currentPage + SIBLING_COUNT, pageCount);

    const shouldShowLeftDots = leftSiblingIndex >= 2;
    const shouldShowRightDots = rightSiblingIndex <= pageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = pageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 2 * SIBLING_COUNT;

      let leftRange = range(1, leftItemCount);
      if (leftItemCount === pageCount - 1) {
        leftRange = range(1, leftItemCount - 1);
      }

      return [...leftRange, DOTS, pageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 2 * SIBLING_COUNT;
      let rightRange = range(pageCount - rightItemCount + 1, pageCount);
      if (pageCount - rightItemCount === 1) {
        rightRange = range(pageCount - rightItemCount + 2, pageCount);
      }
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return range(1, pageCount);
  }, [currentPage, pageCount]);

  const handlePageChange = useCallback(
    (page: number | string) => () => {
      if (page === DOTS) {
        return;
      }

      onPageChange(page as number);
    },
    [onPageChange]
  );

  const handleNextClick = useCallback(() => {
    onPageChange(currentPage + 1);
  }, [currentPage, onPageChange]);

  const handlePrevClick = useCallback(() => {
    onPageChange(currentPage - 1);
  }, [currentPage, onPageChange]);

  return {
    pagesRange,
    handlePageChange,
    handleNextClick,
    handlePrevClick,
  };
};

export default useManagePagination;
